/** Axios instance: httpOnly-куки + CSRF-заголовок + auto-refresh interceptor. */

import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/stores/auth";

const SAFE_METHODS = new Set(["get", "head", "options"]);

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 15000,
  withCredentials: true,
});

// Request: CSRF-заголовок на мутирующие методы (сессия — в httpOnly-куках)
api.interceptors.request.use((config) => {
  const csrf = useAuthStore.getState().csrfToken;
  if (csrf && !SAFE_METHODS.has((config.method ?? "get").toLowerCase())) {
    config.headers.set("X-CSRF-Token", csrf);
  }
  return config;
});

// Response: при 401 (протух access) или CSRF-403 — singleton refresh и retry
let refreshPromise: Promise<void> | null = null;

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;
    // 403 с CSRF-текстом: куки живы, а csrf-токен потерян/протух — refresh выдаст новый
    const isCsrfError =
      status === 403 && JSON.stringify(error.response?.data ?? "").includes("CSRF");
    const isAuthCall =
      (original?.url ?? "").includes("/auth/login") ||
      (original?.url ?? "").includes("/auth/refresh");

    if ((status === 401 || isCsrfError) && original && !original._retried && !isAuthCall) {
      original._retried = true;

      // Singleton refresh: если уже идёт — ждём его
      if (!refreshPromise) {
        refreshPromise = useAuthStore
          .getState()
          .refresh()
          .finally(() => {
            refreshPromise = null;
          });
      }

      try {
        await refreshPromise;
        const csrf = useAuthStore.getState().csrfToken;
        if (csrf && !SAFE_METHODS.has((original.method ?? "get").toLowerCase())) {
          original.headers.set("X-CSRF-Token", csrf);
        }
        return api(original);
      } catch (e) {
        useAuthStore.getState().logout();
        // Через window.location, не через react-router (interceptor вне React);
        // guard от reload-цикла, когда уже на /login
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
        return Promise.reject(e);
      }
    }

    return Promise.reject(error);
  },
);
